<script setup lang="ts">
import { computed, ref, watch, onBeforeMount } from "vue";
import MesasListado from "./components/MesasListado.vue";
import MesasMapaSalon from "./components/MesasMapaSalon.vue";
import MesasAgregar from "./components/MesasAgregar.vue";
import Button from "../../base-components/Button";
import Lucide from "../../base-components/Lucide/Lucide.vue";
import { useSectoresStore } from "../../services/sectores";
import { userUserStore } from "../../stores/user";
import ModalSectores from "./components/modals/ModalSectores.vue";

const vista = ref<"listado" | "mapa">("listado");
const showFormAgregar = ref(false);
const showModalSectores = ref(false);
const sectoresStore = useSectoresStore();
const userStore = userUserStore();

const generalSectorId = computed(() => {
  const sectores = sectoresStore.sectores ?? [];
  return (
    sectores.find(
      s => (s.nombre ?? "").trim().toLowerCase() === "general"
    )?.id ?? sectores[0]?.id ?? ""
  );
});

/** Sector seleccionado para el mapa (debe ser un id real de `empresas/{id}/sectores`). */
const sectorIdSeleccionado = ref<string>("");

onBeforeMount(() => {
  if (userStore.empresa?.id) sectoresStore.getFromFirestore();
});
watch(
  () => userStore.empresa?.id,
  id => {
    if (id) sectoresStore.getFromFirestore();
  }
);
watch(
  () => vista.value,
  v => {
    if (v !== "mapa") return;
    if (!sectorIdSeleccionado.value && sectoresStore.sectores.length > 0) {
      sectorIdSeleccionado.value = generalSectorId.value;
    }
  }
);

watch(
  () => sectoresStore.sectores,
  (sectores: typeof sectoresStore.sectores) => {
    if (!sectorIdSeleccionado.value && sectores?.length) {
      sectorIdSeleccionado.value = generalSectorId.value;
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="mt-5">
    <div class="flex flex-wrap items-center gap-2 mb-4">
      <Button
        :variant="vista === 'listado' ? 'primary' : 'outline-secondary'"
        @click="vista = 'listado'"
      >
        Listado
      </Button>
      <Button
        :variant="vista === 'mapa' ? 'primary' : 'outline-secondary'"
        @click="vista = 'mapa'"
      >
        Mapa del salón
      </Button>
      <Button
        v-if="vista === 'listado' && !showFormAgregar"
        variant="primary"
        class="ml-auto"
        @click="showFormAgregar = true"
      >
        Crear mesa
      </Button>
    </div>
    <MesasAgregar
      v-if="showFormAgregar"
      @cerrar="showFormAgregar = false"
    />
    <template v-else>
      <MesasListado v-if="vista === 'listado'" />
      <template v-else>
        <div class="flex flex-wrap items-center gap-2 mb-3">
          <span class="text-xs font-medium text-slate-600 dark:text-slate-300">Sector:</span>
          <Button
            v-for="s in sectoresStore.sectores"
            :key="s.id"
            :variant="sectorIdSeleccionado === s.id ? 'primary' : 'outline-secondary'"
            size="sm"
            @click="sectorIdSeleccionado = s.id"
          >
            {{ s.nombre }}
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            class="ml-2 border-dashed"
            @click="showModalSectores = true"
          >
            <Lucide icon="Pencil" class="w-4 h-4 mr-1.5 shrink-0" />
            Gestionar sectores
          </Button>
        </div>
        <MesasMapaSalon :sector-id="sectorIdSeleccionado" />
        <ModalSectores v-if="showModalSectores" @cerrar="showModalSectores = false" />
      </template>
    </template>
  </div>
</template>
